import { Card, CardContent } from "@/shared/ui/card";

import { engineLabel, type GeoProbe, type GeoReport } from "../../../api/geoService";

function Bar({ value, tone }: { value: number; tone: "primary" | "success" }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${tone === "success" ? "bg-emerald-500" : "bg-primary"}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">
        {Math.round(value)}%
      </span>
    </div>
  );
}

function rankStats(probes: GeoProbe[]): { avg: number; n: number } | null {
  const ranks = probes.map((p) => p.rank).filter((r): r is number => typeof r === "number");
  if (ranks.length === 0) return null;
  return { avg: ranks.reduce((a, b) => a + b, 0) / ranks.length, n: ranks.length };
}

/** 一句人话点评(确定性规则,不走 LLM)。排位样本 <3 次不下「常放前排」结论(防幸存者假象)。 */
function verdict(
  mentionRate: number,
  positiveRate: number,
  rank: { avg: number; n: number } | null
): string {
  const rankSolid = rank != null && rank.n >= 3;
  const rankNote = rankSolid && rank.avg <= 2.5 ? ",常放前排" : rankSolid ? ",但排位靠后" : "";
  if (mentionRate >= 60) return `常提到你${rankNote}`;
  if (mentionRate >= 30) {
    return positiveRate >= mentionRate / 2
      ? `偶尔提到你,口碑不错${rankNote}`
      : `偶尔提到你,评价平平`;
  }
  return "基本不提你,重点突破对象";
}

/** 分引擎表现矩阵:每个 AI 平台分别怎么看你(提及率/正面率/平均排位)。 */
export function PlatformMatrix({ report }: { report: GeoReport }) {
  if (report.platform_scores.length === 0) return null;
  const probesByPlat = new Map<string, GeoProbe[]>();
  for (const p of report.probes ?? []) {
    const arr = probesByPlat.get(p.platform) ?? [];
    arr.push(p);
    probesByPlat.set(p.platform, arr);
  }
  const rows = [...report.platform_scores].sort((a, b) => b.mention_rate - a.mention_rate);
  return (
    <Card>
      <CardContent className="p-6">
        <div className="text-sm font-medium">分引擎表现矩阵</div>
        <div className="mt-1 text-xs text-muted-foreground">
          每个 AI 平台分别怎么看你 —— 提及率 = 该引擎的提问里提到你的比例;正面率 =
          说你好话的比例;平均排位 = 进推荐名单时你排第几
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" data-testid="geo-platform-matrix">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 font-medium">引擎</th>
                <th className="py-2 pr-3 font-medium">提及率</th>
                <th className="py-2 pr-3 font-medium">正面率</th>
                <th className="py-2 pr-3 font-medium">平均排位</th>
                <th className="py-2 font-medium">一句话点评</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const rank = rankStats(probesByPlat.get(p.platform) ?? []);
                return (
                  <tr key={p.platform} className="border-b border-border/50 last:border-0">
                    <td className="py-2.5 pr-3 font-medium">{engineLabel(p.platform)}</td>
                    <td className="py-2.5 pr-3">
                      <Bar value={p.mention_rate} tone="primary" />
                    </td>
                    <td className="py-2.5 pr-3">
                      <Bar value={p.positive_rate ?? 0} tone="success" />
                    </td>
                    <td className="py-2.5 pr-3 tabular-nums">
                      {rank == null ? (
                        "—"
                      ) : (
                        <>
                          {rank.avg.toFixed(1)}
                          <span className="text-xs text-muted-foreground"> 位</span>
                          {/* 样本量标注:排位只在被提及的回答上算,样本少时防「仅 1 次也排第 1」假象 */}
                          <span className="ml-1 text-xs text-muted-foreground">
                            (基于{rank.n}次)
                          </span>
                        </>
                      )}
                    </td>
                    <td className="py-2.5 text-xs text-muted-foreground">
                      {verdict(p.mention_rate, p.positive_rate ?? 0, rank)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
